// The in-memory API's Founder OS (FOUNDER-OS; 12, ADR-0013): the switcher's company list
// (`me.companies`), the account and its portfolio company (`accounts.create`), adding companies to
// the portfolio, the agency's home company claim, and the portfolio views — Focus, the portfolio
// chart with each company's head seat linked under the founder seat, the escalated issues, the
// cascade of portfolio priorities and the founder brief. The rules are services/api's
// (domain/portfolio/views.ts, brief.ts):
//   - `me.companies`: the portfolio first, then the companies the caller owns or works in
//     (account position, then name), then those reached through an agency grant or held by the
//     account as a client ("Clients via HTVA"), by position then name; `band` and `position` only
//     for companies in the caller's account;
//   - a portfolio route runs on the portfolio company (`co`); any account grant holder reads, the
//     account owner writes; a `portfolio_viewer` reads KPI, finance and goals only;
//   - Focus rows: one per company of the account, `needs_you` from the exceptions (KPI band low or
//     average, off-track or at-risk priorities, open escalations, actual over budget, approvals
//     waiting, an overdue meeting cadence), rows with exceptions first (most first), then A–Z;
//     `trend` compares the last two KPI weeks (scores 0–100 like the engine's; ±2 points = flat);
//   - goals: the portfolio company's priorities of the quarter with their links to company
//     priorities (read live from each company's goals) and the roll-up (achieved = 1, dropped or
//     missed = 0, open = its milestone share, or 0.5 without milestones);
//   - the brief: "What's off", "What changed", "Decisions waiting", "Coming up", and a headline.
// FOUNDER-OS phase 1: what each company shares (`settings.founder_sharing`, else the default by
// relation) — a withheld kind reads as `withheld`, never a stale number; the chart's lenses per
// card, re-parenting a card (owner only), the roll-up scorecard (13 weeks, the CEO seat's and the
// company's metrics as shared) and Reports › Live; `vacancy` and `stale` needs-you chips.
// The demo world (on the founder's first read): HTVA's CEO seat held by the founder with two
// priorities, two of Acme's priorities off track, an issue escalated to the portfolio by Acme and
// one by Beta, two portfolio priorities linked into Acme and HTVA, and yesterday's brief. KPI,
// finance, agent and meeting numbers are fixed projections per company.
import type { Problem, RouteId } from "@companyos/sdk";
import { formatScore } from "../features/format";
import { isoWeekKey } from "../focus/focus";
import type { Handler, Principal } from "./fake";
import type { CadencePortfolioBridge } from "./fake-cadence";
import { COMPANY_IDS, type FakeCompany, uid } from "./fake-data";
import type {
  AccountRef,
  CascadeLink,
  EscalationItem,
  FocusRow,
  FounderBrief,
  FounderBriefLine,
  FounderSharing,
  FounderSharingView,
  Goal,
  GoalLink,
  HeadSeat,
  LinkedSeatRef,
  MeCompanies,
  MeResponse,
  NeedsYou,
  PortfolioChart,
  PortfolioCompany,
  PortfolioFocus,
  PortfolioGoal,
  PortfolioLive,
  PortfolioMetric,
  PortfolioScorecard,
  Seat,
  SeatAssignment,
  SwitcherCompany,
} from "./types";

/** Every route this twin serves (the contract test checks it is all of `portfolioRoutes`). */
export const FAKE_PORTFOLIO_ROUTES = [
  "companies.founderSharing",
  "me.companies",
  "accounts.create",
  "accounts.companies.add",
  "portfolio.companies.list",
  "portfolio.companies.remove",
  "portfolio.focus",
  "portfolio.chart",
  "portfolio.linkedSeats.update",
  "portfolio.scorecard",
  "portfolio.live",
  "portfolio.escalations",
  "portfolio.goals",
  "portfolio.goalLinks.create",
  "portfolio.goalLinks.delete",
  "portfolio.briefs.list",
  "portfolio.briefs.generate",
] as const satisfies readonly RouteId[];

/** The demo founder's ids (fake.ts's `founder@htva.example`). */
export const PORTFOLIO = {
  companyId: "0193a0a0-0000-7000-8000-00000000f05e",
  slug: "founder-os",
  accountId: uid(0xacc0, 1),
  founderUserId: "0193a0a0-0000-7000-8000-0000000000f6",
  founderName: "Frances Founder",
  /** The founder's membership in Founder OS, and in HTVA (its owner). */
  founderMembership: uid(0xf0f0, 1),
  htvaMembership: uid(0xf0f0, 2),
  founderSeat: uid(0xf05e, 1),
  chiefSeat: uid(0xf05e, 2),
  htvaCeoSeat: uid(0xf05e, 3),
} as const;

/** The demo agency's display name ("Clients via HTVA"); the fake's one agency. */
export const AGENCY_NAMES: Record<string, string> = {
  "0193a0a0-0000-7000-8000-00000000a9e1": "HTVA",
};

const EPOCH = "2026-06-01T09:00:00Z";
const DAY = 86_400_000;
const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/** The Founder OS company (`kind = portfolio`) of the demo account. */
export function portfolioCompany(
  id: string = PORTFOLIO.companyId,
  slug: string = PORTFOLIO.slug,
): FakeCompany {
  return {
    id,
    slug,
    name: "Founder OS",
    kind: "portfolio",
    settings: {},
    calendar_config: { timezone: "America/New_York", weekStartDay: 1, fiscalYearStartMonth: 1 },
    reporting_currency: "USD",
    agent_mode: "self_serve",
    created_at: EPOCH,
    updated_at: EPOCH,
  };
}

// Fixed projections (12: the control plane's `portfolio_view_cache`) ---------------------------

type Projection = {
  band: FocusRow["band"];
  score: number;
  previous: number;
  budget_minor: number;
  actual_minor: number;
  runs_7d: number;
  spend_month_micros: number;
  approvals: number;
  next_meeting_days: number | null;
  last_meeting_days: number | null;
  unread: number;
  /** FOUNDER-OS phase 1: the scores of the weeks before `previous`, oldest first. */
  history: number[];
  completeness: number;
  /** The CEO seat's and the company's metrics of the newest week (the client view). */
  head_metrics: PortfolioMetric[];
  company_metrics: PortfolioMetric[];
};

/** A metric line of the `kpi` projection; hit from the value against the target. */
const metric = (
  key: string,
  name: string,
  value: number,
  target: number,
  direction: "up" | "down" = "up",
  unit = "count",
): PortfolioMetric => {
  const hit = direction === "up" ? value >= target : value <= target;
  return {
    key,
    name,
    unit,
    direction,
    value,
    target,
    hit,
    health: hit ? "green" : "red",
    trend: "flat",
  };
};

const PROJECTIONS: Record<string, Projection> = {
  // HTVA: healthy — great band, inside budget, a meeting every week.
  [COMPANY_IDS.htva]: {
    history: [88, 90, 93, 95],
    completeness: 1,
    head_metrics: [metric("pilots-signed", "Pilot clients signed", 3, 3)],
    company_metrics: [
      metric("placements", "Placements", 4, 4),
      metric("weekly-meetings", "Weekly meetings held", 1, 1),
    ],
    band: "great",
    score: 100,
    previous: 96,
    budget_minor: 4_200_000,
    actual_minor: 3_910_000,
    runs_7d: 0,
    spend_month_micros: 0,
    approvals: 0,
    next_meeting_days: 3,
    last_meeting_days: 4,
    unread: 1,
  },
  // Acme: a red KPI falling, over budget (and two priorities off track, an escalation: live).
  [COMPANY_IDS.acme]: {
    history: [74, 71, 68, 66],
    completeness: 0.8,
    head_metrics: [metric("time-to-offer", "Time to offer (days)", 27, 21, "down")],
    company_metrics: [
      metric("placements", "Placements", 6, 10),
      metric("shortlists", "Shortlists sent", 12, 15),
    ],
    band: "low",
    score: 54,
    previous: 63,
    budget_minor: 8_000_000,
    actual_minor: 8_960_000,
    runs_7d: 46,
    spend_month_micros: 182_400_000,
    approvals: 0,
    next_meeting_days: 1,
    last_meeting_days: 6,
    unread: 3,
  },
  // Beta: a fair week, approvals waiting (and an escalation: live).
  [COMPANY_IDS.beta]: {
    history: [79, 80, 80, 82],
    completeness: 0.9,
    head_metrics: [metric("shortlists", "Shortlists sent", 9, 8)],
    company_metrics: [metric("placements", "Placements", 5, 6)],
    band: "regular",
    score: 82,
    previous: 81,
    budget_minor: 5_000_000,
    actual_minor: 4_400_000,
    runs_7d: 12,
    spend_month_micros: 41_000_000,
    approvals: 2,
    next_meeting_days: 5,
    last_meeting_days: 2,
    unread: 0,
  },
};

// Sharing with Founder OS (FOUNDER-OS phase 1, design §2.3) ------------------------------------

/** Defaults by how the portfolio holds the company (the company's setting overrides). */
export const SHARING_DEFAULTS: Record<"owned" | "client", FounderSharing> = {
  owned: {
    kpi: "metrics",
    finance: "totals",
    goals: true,
    issues: true,
    escalation: true,
    decisions: true,
    headcount: true,
    meetings: true,
  },
  client: {
    kpi: "scores",
    finance: "off",
    goals: true,
    issues: false,
    escalation: true,
    decisions: false,
    headcount: false,
    meetings: false,
  },
};

/** The projection kinds the company withholds (`{withheld: true}` snapshots). */
function withheldOf(s: FounderSharing): NonNullable<FocusRow["withheld"]> {
  return [
    ...(s.kpi === "off" ? (["kpi"] as const) : []),
    ...(s.finance === "off" ? (["finance"] as const) : []),
    ...(["goals", "issues", "escalation", "decisions", "headcount", "meetings"] as const).filter(
      (k) => !s[k],
    ),
  ];
}

// State --------------------------------------------------------------------------------------

type Holding = {
  company_id: string;
  relation: "owned" | "client";
  position: number;
  added_at: string;
  /**
   * FOUNDER-OS: the company's live `cp.linked_seat` (its id and the founder-chart seat it hangs
   * under); absent while the company has no link (its card then carries the company id).
   */
  link?: { id: string; parent: string };
};
type Level = "owner" | "portfolio_member" | "portfolio_viewer";
type Account = {
  id: string;
  name: string;
  owner_user_id: string;
  portfolio_company_id: string;
  founder_membership: string;
  founder_seat: string;
  chief_seat: string;
  holdings: Holding[];
  links: GoalLink[];
  briefs: FounderBrief[];
  seeded: boolean;
  /** A grant's company subset, by user id (absent: every company of the account). */
  subsets: Record<string, string[]>;
  /** When a company was last taken out (older briefs' headlines may count it). */
  removed_at?: string;
};

export type PortfolioDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  now: () => Date;
  /** The twin's companies (mutable: an account's new portfolio company is pushed). */
  companies: FakeCompany[];
  seatsOf: (c: FakeCompany) => Seat[];
  assignmentsOf: (c: FakeCompany) => SeatAssignment[];
  goalsOf: (c: FakeCompany) => Goal[];
  /** The company's digital employees (`status` = active is live). */
  desOf: (companyId: string) => { status: string }[];
  nameOf: (c: FakeCompany, membershipId: string) => string;
  cadence: () => CadencePortfolioBridge | null;
};

export function createPortfolioFake(d: PortfolioDeps) {
  const { json, problem, notFound, validation } = d;
  const forbidden = (detail?: string) => problem(403, "forbidden", "Forbidden", detail);
  const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
  const noContent = () => new Response(null, { status: 204 });
  const stamp = () => d.now().toISOString();
  let seq = 0;

  const accounts: Account[] = [
    {
      id: PORTFOLIO.accountId,
      name: PORTFOLIO.founderName,
      owner_user_id: PORTFOLIO.founderUserId,
      portfolio_company_id: PORTFOLIO.companyId,
      founder_membership: PORTFOLIO.founderMembership,
      founder_seat: PORTFOLIO.founderSeat,
      chief_seat: PORTFOLIO.chiefSeat,
      holdings: [
        { company_id: COMPANY_IDS.htva, relation: "owned", position: 0, added_at: EPOCH },
        { company_id: COMPANY_IDS.acme, relation: "client", position: 1, added_at: EPOCH },
        { company_id: COMPANY_IDS.beta, relation: "client", position: 2, added_at: EPOCH },
      ],
      links: [],
      briefs: [],
      seeded: false,
      subsets: {},
    },
  ];

  const companyOf = (id: string) => d.companies.find((c) => c.id === id) ?? null;

  // The demo world's clients agreed to share everything (their owners set it), so their fixed
  // projections cross whole; a client added later keeps the client default.
  for (const h of accounts[0]?.holdings ?? []) {
    const c = companyOf(h.company_id);
    if (c && h.relation === "client" && PROJECTIONS[c.id] && !c.settings.founder_sharing)
      c.settings = { ...c.settings, founder_sharing: SHARING_DEFAULTS.owned };
  }
  // The demo companies were added with their head seat linked under the founder.
  for (const a of accounts)
    for (const h of a.holdings)
      h.link = {
        id: uid(0xf1a0, companyNo(a.portfolio_company_id) * 64 + h.position + 1),
        parent: a.founder_seat,
      };

  /** The caller's company subset on the account (none: every company). */
  const inSubset = (a: Account, me: MeResponse, companyId: string) => {
    const subset = a.subsets[me.user.id];
    return !subset || subset.includes(companyId);
  };
  const holdingsFor = (a: Account, me: MeResponse) =>
    a.holdings.filter((h) => inSubset(a, me, h.company_id));
  const accountByCompany = (co: string) =>
    accounts.find((a) => a.portfolio_company_id === co) ?? null;
  const quarterOf = (at: Date) => `${at.getUTCFullYear()}-Q${Math.floor(at.getUTCMonth() / 3) + 1}`;

  /** A manager's view of a company, for reading its stores as the projections do. */
  const systemPrincipal = (me: MeResponse, c: FakeCompany): Principal => ({
    me,
    company: c,
    roles: ["owner"],
    operator: false,
    manager: true,
  });

  // Seeds ------------------------------------------------------------------------------------

  const seatRow = (
    c: FakeCompany,
    id: string,
    title: string,
    supervisor: string | null,
    status: Seat["status"],
  ): Seat => ({
    company_id: c.id,
    id,
    role_id: uid(0x2f00, companyNo(c.id)),
    title,
    supervisor_seat_id: supervisor,
    staff: false,
    kind: "standard",
    status,
    origin: "template",
    origin_ref: null,
    settings: {},
    de_fill: null,
    version: 1,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  });
  const assignmentRow = (
    c: FakeCompany,
    n: number,
    seatId: string,
    membershipId: string,
  ): SeatAssignment => ({
    company_id: c.id,
    id: uid(0xf0a5, companyNo(c.id) * 16 + n),
    seat_id: seatId,
    membership_id: membershipId,
    membership_kind: "person",
    share: "1",
    active_from: EPOCH,
    active_to: null,
    capacity: null,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  });

  /** fake-structure.ts `seedOf`: a portfolio company's founder seat and chief of staff. */
  function structureOf(c: FakeCompany): { seats: Seat[]; assignments: SeatAssignment[] } | null {
    const a = accountByCompany(c.id);
    if (!a) return null;
    return {
      seats: [
        seatRow(c, a.founder_seat, "Founder", null, "filled"),
        seatRow(c, a.chief_seat, "Chief of staff", a.founder_seat, "open"),
      ],
      assignments: [assignmentRow(c, 1, a.founder_seat, a.founder_membership)],
    };
  }

  const goalRow = (
    c: FakeCompany,
    id: string,
    seatId: string,
    quarter: string,
    title: string,
    track: Goal["track"],
    milestones: [string, boolean][],
  ): Goal => ({
    company_id: c.id,
    id,
    kind: "priority",
    seat_id: seatId,
    team_id: null,
    title,
    due_at: null,
    period_key: quarter,
    metric_def_id: null,
    target: null,
    state: "open",
    track,
    flagged_to_portfolio: false,
    portfolio_link_ref: null,
    vision_page_id: null,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
    milestones: milestones.map(([title, done], position) => ({
      company_id: c.id,
      id: uid(0xf0e0 + position, Number.parseInt(id.slice(-6), 16)),
      goal_id: id,
      title,
      due_at: null,
      done_at: done ? EPOCH : null,
      position,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: EPOCH,
      updated_at: EPOCH,
    })),
  });

  /** fake-org.ts `seedGoalsOf`: the portfolio company's priorities of the quarter. */
  function goalsSeedOf(c: FakeCompany, quarter: string): Goal[] | null {
    const a = accountByCompany(c.id);
    if (!a) return null;
    if (a.id !== PORTFOLIO.accountId) return [];
    const n = companyNo(c.id) * 16;
    return [
      goalRow(
        c,
        uid(0xf0d0, n + 1),
        a.founder_seat,
        quarter,
        "Founder OS live in every company",
        "on",
        [
          ["HTVA on the weekly cadence", true],
          ["Acme and Beta onboarded", false],
        ],
      ),
      goalRow(
        c,
        uid(0xf0d0, n + 2),
        a.founder_seat,
        quarter,
        "Get Acme's delivery back on track",
        "off",
        [
          ["COO decision made", false],
          ["Time-to-offer under 21 days", false],
        ],
      ),
      goalRow(
        c,
        uid(0xf0d0, n + 3),
        a.founder_seat,
        quarter,
        "Close the year inside budget",
        "at_risk",
        [],
      ),
    ];
  }

  /** The demo world, once, on the founder's first read. */
  function ensureWorld(a: Account, me: MeResponse) {
    if (a.seeded || a.id !== PORTFOLIO.accountId) return;
    a.seeded = true;
    const quarter = quarterOf(d.now());
    // HTVA: the founder holds its CEO seat, with two priorities on track.
    const htva = companyOf(COMPANY_IDS.htva);
    if (htva) {
      const seats = d.seatsOf(htva);
      if (!seats.some((s) => s.id === PORTFOLIO.htvaCeoSeat)) {
        seats.push(seatRow(htva, PORTFOLIO.htvaCeoSeat, "CEO", null, "filled"));
        d.assignmentsOf(htva).push(
          assignmentRow(htva, 1, PORTFOLIO.htvaCeoSeat, PORTFOLIO.htvaMembership),
        );
      }
      const goals = d.goalsOf(htva);
      const mine = goals.filter((g) => g.seat_id === PORTFOLIO.htvaCeoSeat);
      const specs: [string, Goal["track"], [string, boolean][]][] = [
        [
          "Sign three Founder OS pilot clients",
          "on",
          [
            ["Pilot offer written", true],
            ["Three signed", false],
          ],
        ],
        ["Every client meeting weekly in CompanyOS", "on", [["Acme's first weekly", true]]],
      ];
      specs.forEach(([title, track, ms], i) => {
        const g = mine[i];
        if (g) Object.assign(g, { title, track, state: "open" });
        else
          goals.push(
            goalRow(htva, uid(0xf0c1, i + 1), PORTFOLIO.htvaCeoSeat, quarter, title, track, ms),
          );
      });
      for (const g of mine.slice(specs.length)) g.state = "dropped";
    }
    // Acme: two priorities off track.
    const acme = companyOf(COMPANY_IDS.acme);
    if (acme) {
      const open = d
        .goalsOf(acme)
        .filter((g) => g.kind === "priority" && g.period_key === quarter && g.state === "open");
      const ranked = [
        ...open.filter((g) => g.track !== "on"),
        ...open.filter((g) => g.track === "on"),
      ];
      for (const g of ranked.slice(0, 2)) g.track = "off";
    }
    // An issue escalated to the portfolio by Acme, one by Beta.
    const bridge = d.cadence();
    const beta = companyOf(COMPANY_IDS.beta);
    if (bridge && acme)
      bridge.seedEscalated(systemPrincipal(me, acme), {
        title: "Northwind may pull the controller search",
        body: "Two missed shortlists; their CFO wants a call with the founder this week.",
        priority: 1,
        status: "discuss",
        daysAgo: 2,
      });
    if (bridge && beta)
      bridge.seedEscalated(systemPrincipal(me, beta), {
        title: "Payroll vendor renewal is 30% over budget",
        body: "Renew for a year, or move payroll to HTVA's provider in Q1?",
        priority: 2,
        status: "identify",
        daysAgo: 1,
      });
    // Two portfolio priorities cascaded into HTVA and Acme.
    const portfolio = companyOf(a.portfolio_company_id);
    if (portfolio) {
      const pgoals = d.goalsOf(portfolio).filter((g) => g.kind === "priority");
      const htvaGoal = htva
        ? d.goalsOf(htva).find((g) => g.seat_id === PORTFOLIO.htvaCeoSeat && g.state === "open")
        : undefined;
      const acmeOff = acme
        ? d.goalsOf(acme).filter((g) => g.period_key === quarter && g.track === "off")
        : [];
      const link = (pg: Goal | undefined, companyId: string, g: Goal | undefined) => {
        if (pg && g)
          a.links.push({
            id: uid(0xf011, a.links.length + 1),
            portfolio_goal_id: pg.id,
            company_id: companyId,
            company_goal_id: g.id,
            created_at: EPOCH,
          });
      };
      link(pgoals[0], COMPANY_IDS.htva, htvaGoal);
      link(pgoals[1], COMPANY_IDS.acme, acmeOff[0]);
    }
    // Yesterday's brief.
    const brief = composeBrief(focusOf(a, me), portfolioCounts(a));
    const at = new Date(d.now().getTime() - DAY);
    at.setUTCHours(11, 0, 0, 0);
    a.briefs.push({
      company_id: a.portfolio_company_id,
      id: uid(0xf0b0, 1),
      cadence: "daily",
      period_key: at.toISOString().slice(0, 10),
      generated_by: "system",
      ...brief,
      created_at: at.toISOString(),
    });
  }

  // Standing ---------------------------------------------------------------------------------

  /** The caller's grant on an account (null: none). */
  const levelOn = (me: MeResponse, a: Account) =>
    me.portfolio && me.portfolio.account_id === a.id ? me.portfolio.level : null;

  /**
   * Account-wide writes (add, remove, cascade, brief, move, reorder) need an owner grant on the
   * whole account; an owner grant limited to a company subset reads only.
   */
  const writesOn = (me: MeResponse, a: Account) =>
    levelOn(me, a) === "owner" && !a.subsets[me.user.id];

  /** A portfolio route's account and the caller's level, or the error response. */
  function standing(
    p: Principal | null,
    write = false,
  ): { a: Account; level: "owner" | "portfolio_member" | "portfolio_viewer" } | Response {
    if (!p) return notFound();
    const a = accountByCompany(p.company.id);
    if (!a) return notFound();
    const level = levelOn(p.me, a);
    if (!level) return notFound();
    // A write needs both: a manager of the portfolio company and an account-wide owner grant.
    if (write && !p.manager) return forbidden("A manager of the portfolio company only.");
    if (write && !writesOn(p.me, a)) return forbidden("The account owner only.");
    ensureWorld(a, p.me);
    return { a, level };
  }

  // Sharing ----------------------------------------------------------------------------------

  /**
   * What the company shares (services/api's effective sharing): its stored setting merged over
   * a base — the client defaults when any live holding of it, across all accounts, is a client
   * or no account holds it; the owned defaults only when held and every holding is owned.
   */
  function sharingView(c: FakeCompany): FounderSharingView {
    const holdings = accounts.flatMap((a) => a.holdings.filter((h) => h.company_id === c.id));
    const owned = holdings.length > 0 && holdings.every((h) => h.relation === "owned");
    const stored = c.settings.founder_sharing;
    return {
      effective: { ...SHARING_DEFAULTS[owned ? "owned" : "client"], ...stored },
      source: stored ? "setting" : owned ? "owned_default" : "client_default",
      held: holdings.length > 0,
    };
  }
  const sharingOf = (c: FakeCompany) => sharingView(c).effective;

  // Views ------------------------------------------------------------------------------------

  const holderOf = (c: FakeCompany, seatId: string) =>
    d.assignmentsOf(c).find((x) => x.seat_id === seatId && x.active_to === null) ?? null;

  function headSeatOf(c: FakeCompany): HeadSeat | null {
    // FOUNDER-OS phase 1: the seat the company pinned (Setup › Chart), else the heuristic.
    const pinned = d
      .seatsOf(c)
      .find(
        (s) => s.id === c.settings.head_seat_id && (s.status === "open" || s.status === "filled"),
      );
    const roots = d
      .seatsOf(c)
      .filter(
        (s) => s.supervisor_seat_id === null && s.status !== "proposed" && s.status !== "closed",
      );
    const held = roots.map((s) => ({ s, h: holderOf(c, s.id) }));
    const pick = pinned
      ? { s: pinned, h: holderOf(c, pinned.id) }
      : (held.find((x) => x.h?.membership_kind === "person") ?? held.find((x) => x.h) ?? held[0]);
    if (!pick) return null;
    return {
      pinned: !!pinned,
      open_since: pick.h ? null : pick.s.updated_at,
      seat_id: pick.s.id,
      title: pick.s.title,
      holder_name: pick.h ? d.nameOf(c, pick.h.membership_id) : null,
      holder_kind:
        pick.h?.membership_kind === "person" || pick.h?.membership_kind === "digital_employee"
          ? pick.h.membership_kind
          : null,
      supervisor_title: null,
    };
  }

  function headcountOf(c: FakeCompany) {
    const seats = d.seatsOf(c).filter((s) => s.status !== "proposed" && s.status !== "closed");
    const people = new Set(
      d
        .assignmentsOf(c)
        .filter((x) => x.active_to === null && x.membership_kind === "person")
        .map((x) => x.membership_id),
    );
    return {
      people: people.size,
      seats: seats.length,
      open_seats: seats.filter((s) => s.status === "open").length,
    };
  }

  /** The company's priorities of the quarter (its `goals` projection). */
  function prioritiesOf(c: FakeCompany) {
    const quarter = quarterOf(d.now());
    const rows = d.goalsOf(c).filter((g) => g.kind === "priority" && g.period_key === quarter);
    const open = rows.filter((g) => g.state === "open");
    const seats = d.seatsOf(c);
    return {
      quarter,
      total: rows.filter((g) => g.state !== "dropped").length,
      on: open.filter((g) => g.track === "on").length,
      at_risk: open.filter((g) => g.track === "at_risk").length,
      off: open.filter((g) => g.track === "off").length,
      achieved: rows.filter((g) => g.state === "achieved").length,
      off_track: open
        .filter((g) => g.track !== "on")
        .sort((x, y) => Number(y.track === "off") - Number(x.track === "off"))
        .slice(0, 5)
        .map((g) => {
          const h = holderOf(c, g.seat_id);
          return {
            goal_id: g.id,
            title: g.title,
            track: g.track,
            seat_title: seats.find((s) => s.id === g.seat_id)?.title ?? null,
            owner_name: h ? d.nameOf(c, h.membership_id) : null,
          };
        }),
    };
  }

  function escalationsOf(me: MeResponse, c: FakeCompany): EscalationItem[] {
    return (d.cadence()?.escalated(systemPrincipal(me, c)) ?? []).map((i) => ({
      company_id: c.id,
      company_name: c.name,
      company_slug: c.slug,
      issue_id: i.id,
      title: i.title,
      priority: i.priority,
      status: i.status,
      owner_title: i.owner_title,
      owner_name: i.owner_name ?? i.raised_by_name,
      why: i.body,
      escalated_at: i.updated_at,
    }));
  }

  /** `back` weeks before last week's ISO key (0 = last week, the newest scored week). */
  const weekKey = (back: number) => isoWeekKey(new Date(d.now().getTime() - (back + 1) * 7 * DAY));
  /** The newest KPI week of a projection (the fixed demo ones are always last week). */
  const newestWeek = (_pr: Projection) => weekKey(0);
  /** A projection's KPI weeks, oldest first: its history, the previous week and the newest. */
  const weeksOf = (pr: Projection) => {
    const scores = [...pr.history, pr.previous, pr.score];
    return scores.map((score, i) => ({ period_key: weekKey(scores.length - 1 - i), score }));
  };
  const bandOf = (score: number): NonNullable<FocusRow["band"]> =>
    score >= 90 ? "great" : score >= 75 ? "regular" : score >= 60 ? "average" : "low";

  function focusRow(
    me: MeResponse,
    h: Holding,
    c: FakeCompany,
    level: "owner" | "portfolio_member" | "portfolio_viewer",
  ): FocusRow {
    const all = PROJECTIONS[c.id];
    // FOUNDER-OS phase 1: a kind the company withholds crosses as `{withheld: true}`.
    const withheld = withheldOf(sharingOf(c));
    const off = new Set<string>(withheld);
    const pr = off.has("kpi") ? undefined : all;
    const viewer = level === "portfolio_viewer";
    const now = d.now();
    let trend: FocusRow["trend"] = null;
    if (pr) {
      const delta = pr.score - pr.previous;
      trend = Math.abs(delta) <= 2 ? "flat" : delta > 0 ? "up" : "down";
    }
    const priorities = off.has("goals")
      ? {
          quarter: quarterOf(now),
          total: 0,
          on: 0,
          at_risk: 0,
          off: 0,
          achieved: 0,
          off_track: [],
        }
      : prioritiesOf(c);
    const escalations = viewer || off.has("escalation") ? [] : escalationsOf(me, c);
    const finance =
      all && !off.has("finance")
        ? {
            currency: c.reporting_currency,
            period_key: now.toISOString().slice(0, 7),
            budget_minor: all.budget_minor,
            actual_minor: all.actual_minor,
            variance_minor: all.budget_minor - all.actual_minor,
          }
        : null;
    // A viewer reads kpi, finance and goals only; a withheld kind is not read either.
    const noHeadcount = viewer || off.has("headcount");
    const des = noHeadcount ? [] : d.desOf(c.id);
    const approvals = viewer || off.has("decisions") ? 0 : (all?.approvals ?? 0);
    const head = noHeadcount ? null : headSeatOf(c);
    const meetings = !viewer && !off.has("meetings") ? all : undefined;
    const at = (days: number | null | undefined) =>
      days === null || days === undefined
        ? null
        : new Date(now.getTime() + days * DAY).toISOString();
    const needs: NeedsYou[] = [];
    if (pr && (pr.band === "low" || pr.band === "average"))
      needs.push({
        kind: "kpi",
        text: `KPI ${pr.band === "low" ? "off target" : "below target"} (${formatScore(pr.score)})`,
      });
    if (priorities.off + priorities.at_risk > 0)
      needs.push({
        kind: "priority",
        text: [
          priorities.off
            ? `${priorities.off} priorit${priorities.off === 1 ? "y" : "ies"} off track`
            : "",
          priorities.at_risk
            ? `${priorities.at_risk} priorit${priorities.at_risk === 1 ? "y" : "ies"} at risk`
            : "",
        ]
          .filter(Boolean)
          .join(", "),
      });
    if (escalations.length)
      needs.push({
        kind: "escalation",
        text: `${escalations.length} issue${escalations.length === 1 ? "" : "s"} escalated to you`,
      });
    if (finance && finance.actual_minor > (finance.budget_minor ?? Number.POSITIVE_INFINITY))
      needs.push({ kind: "budget", text: "Spend is over this month's budget" });
    if (approvals > 0)
      needs.push({
        kind: "approvals",
        text: `${approvals} approval${approvals === 1 ? "" : "s"} waiting`,
      });
    // FOUNDER-OS phase 1: the head seat is open; the newest KPI week is older than last week.
    if (head && !head.holder_name)
      needs.push({
        kind: "vacancy",
        text: `CEO seat open since ${(head.open_since ?? stamp()).slice(0, 10)}`,
      });
    if (pr && newestWeek(pr) < isoWeekKey(new Date(now.getTime() - 7 * DAY)))
      needs.push({ kind: "stale", text: "No numbers for 7 days" });
    return {
      company_id: c.id,
      slug: c.slug,
      name: c.name,
      relation: h.relation,
      band: pr?.band ?? null,
      kpi_score: pr?.score ?? null,
      kpi_period: pr ? isoWeekKey(new Date(now.getTime() - 7 * DAY)) : null,
      trend,
      priorities,
      escalations: { count: escalations.length, items: escalations.slice(0, 3) },
      finance,
      agents:
        viewer || off.has("headcount")
          ? { des: 0, live: 0, runs_7d: 0, spend_month_micros: 0, approvals_waiting: approvals }
          : {
              des: des.length,
              live: des.filter((x) => x.status === "active").length,
              runs_7d: all?.runs_7d ?? 0,
              spend_month_micros: all?.spend_month_micros ?? 0,
              approvals_waiting: approvals,
            },
      headcount: noHeadcount ? { people: 0, seats: 0, open_seats: 0 } : headcountOf(c),
      meetings: meetings
        ? {
            next_at: at(meetings.next_meeting_days),
            last_at: at(-(meetings.last_meeting_days ?? 0)),
            overdue: false,
          }
        : { next_at: null, last_at: null, overdue: false },
      needs_you: needs,
      updated_at: new Date(now.getTime() - 2 * 3_600_000).toISOString(),
      withheld,
    };
  }

  function focusOf(
    a: Account,
    me: MeResponse,
    level: "owner" | "portfolio_member" | "portfolio_viewer" = "owner",
  ): FocusRow[] {
    return holdingsFor(a, me)
      .flatMap((h) => {
        const c = companyOf(h.company_id);
        return c ? [focusRow(me, h, c, level)] : [];
      })
      .sort(
        (x, y) =>
          y.needs_you.length - x.needs_you.length ||
          x.name.localeCompare(y.name) ||
          x.company_id.localeCompare(y.company_id),
      );
  }

  function portfolioGoalsOf(a: Account, quarter: string) {
    const c = companyOf(a.portfolio_company_id);
    return c ? d.goalsOf(c).filter((g) => g.kind === "priority" && g.period_key === quarter) : [];
  }

  /** `total`: the quarter's priorities in every state; on / at risk / off: the open ones. */
  function portfolioCounts(a: Account) {
    const all = portfolioGoalsOf(a, quarterOf(d.now()));
    const goals = all.filter((g) => g.state === "open");
    return {
      total: all.length,
      on: goals.filter((g) => g.track === "on").length,
      at_risk: goals.filter((g) => g.track === "at_risk").length,
      off: goals.filter((g) => g.track === "off").length,
    };
  }

  const progressOf = (g: Goal): number => {
    if (g.state === "achieved") return 1;
    if (g.state === "dropped" || g.state === "missed") return 0;
    if (g.milestones.length === 0) return 0.5;
    return g.milestones.filter((m) => m.done_at).length / g.milestones.length;
  };

  /** The links the reader sees: companies in their subset that share their goals. */
  function cascadeOf(a: Account, pg: Goal, me: MeResponse): CascadeLink[] {
    return a.links
      .filter((l) => l.portfolio_goal_id === pg.id && inSubset(a, me, l.company_id))
      .flatMap((l) => {
        const c = companyOf(l.company_id);
        if (!c || !sharingOf(c).goals) return [];
        const g = d.goalsOf(c).find((x) => x.id === l.company_goal_id);
        return [
          {
            link_id: l.id,
            company_id: c.id,
            company_name: c.name,
            company_slug: c.slug,
            goal_id: l.company_goal_id,
            title: g?.title ?? null,
            track: g?.track ?? null,
            state: g?.state ?? null,
            progress: g ? Math.round(progressOf(g) * 100) / 100 : null,
          },
        ];
      });
  }

  function goalView(a: Account, g: Goal, me: MeResponse): PortfolioGoal {
    const links = cascadeOf(a, g, me);
    const known = links.map((l) => l.progress).filter((x): x is number => x !== null);
    const c = companyOf(a.portfolio_company_id);
    return {
      goal_id: g.id,
      title: g.title,
      track: g.track,
      state: g.state,
      period_key: g.period_key,
      seat_title: (c && d.seatsOf(c).find((s) => s.id === g.seat_id)?.title) ?? null,
      progress: known.length
        ? Math.round((known.reduce((x, y) => x + y, 0) / known.length) * 100) / 100
        : null,
      links,
    };
  }

  /**
   * A company of the account as the reader sees it: the head seat only from the company's shared
   * headcount (none when withheld, or for a viewer, whose grant does not read headcount).
   */
  function portfolioCompanyRow(h: Holding, level: Level): PortfolioCompany | null {
    const c = companyOf(h.company_id);
    if (!c) return null;
    const head = level !== "portfolio_viewer" && sharingOf(c).headcount;
    return {
      company_id: c.id,
      slug: c.slug,
      name: c.name,
      relation: h.relation,
      position: h.position,
      added_at: h.added_at,
      head_seat: head ? headSeatOf(c) : null,
    };
  }

  /** A card's id: its `cp.linked_seat` id, or the company id while it has no link. */
  const linkIdOf = (h: Holding) => h.link?.id ?? h.company_id;

  /**
   * Link a company's head seat under a founder-chart seat (the real API's rule): only from the
   * head seat the company shares — none when its headcount is withheld or it has no head.
   */
  const makeLink = (h: Holding, c: FakeCompany, parent: string): boolean => {
    if (!sharingOf(c).headcount || !headSeatOf(c)) return false;
    h.link = { id: uid(0xf1a0, 0x1000 + ++seq), parent };
    return true;
  };

  // The roll-up scorecard and Reports › Live (FOUNDER-OS phase 1) ---------------------------

  const held = (a: Account, me: MeResponse) =>
    [...holdingsFor(a, me)]
      .sort((x, y) => x.position - y.position)
      .flatMap((h) => {
        const c = companyOf(h.company_id);
        return c ? [{ h, c }] : [];
      });

  function scorecardOf(a: Account, me: MeResponse, level: Level): PortfolioScorecard {
    const rows = new Map(focusOf(a, me, level).map((r) => [r.company_id, r]));
    const list = held(a, me).flatMap(({ h, c }) => {
      const r = rows.get(c.id);
      return r ? [{ h, c, r, share: sharingOf(c) }] : [];
    });
    // The newest ≤13 week keys any company has, oldest first.
    const keys = new Set<string>();
    for (const { c } of list) {
      const pr = PROJECTIONS[c.id];
      if (pr) for (const w of weeksOf(pr)) keys.add(w.period_key);
    }
    const weeks = [...keys].sort().slice(-13);
    return {
      weeks,
      companies: list.map(({ c, r, share }) => {
        const pr = PROJECTIONS[c.id];
        const withheld = share.kpi === "off";
        const byWeek = new Map(pr ? weeksOf(pr).map((w) => [w.period_key, w.score]) : []);
        const metrics = share.kpi === "metrics" && pr;
        return {
          company_id: c.id,
          slug: c.slug,
          name: c.name,
          relation: r.relation,
          band: r.band,
          kpi_score: r.kpi_score,
          completeness: pr && !withheld ? pr.completeness : null,
          trend: r.trend,
          kpi_period: r.kpi_period,
          weeks: weeks.map((k) => {
            const score = byWeek.get(k);
            if (score === undefined) return null;
            return withheld
              ? { period_key: k, kpi_score: null, band: null, completeness: null, withheld: true }
              : {
                  period_key: k,
                  kpi_score: score,
                  band: bandOf(score),
                  completeness: pr?.completeness ?? null,
                  withheld: false,
                };
          }),
          // A viewer reads kpi, finance and goals only: no headcount, so no head seat.
          head_seat: share.headcount && level !== "portfolio_viewer" ? headSeatOf(c) : null,
          head_metrics: metrics ? pr.head_metrics : [],
          company_metrics: metrics ? pr.company_metrics : [],
          sharing: withheld ? "withheld" : share.kpi === "scores" ? "scores" : "metrics",
          updated_at: r.updated_at,
        };
      }),
    };
  }

  function liveOf(a: Account, me: MeResponse, level: Level): PortfolioLive {
    const rows = focusOf(a, me, level);
    const card = new Map(scorecardOf(a, me, level).companies.map((x) => [x.company_id, x]));
    const ordered = held(a, me).flatMap(({ c }) => rows.filter((r) => r.company_id === c.id));
    const ref = (r: FocusRow) => ({ company_id: r.company_id, slug: r.slug, name: r.name });
    const off = (r: FocusRow, kind: string) => (r.withheld ?? []).includes(kind as never);
    const totals = new Map<string, PortfolioLive["money"]["totals"][number]>();
    for (const r of ordered) {
      if (!r.finance) continue;
      const t = totals.get(r.finance.currency) ?? {
        currency: r.finance.currency,
        budget_minor: 0,
        actual_minor: 0,
        variance_minor: 0,
      };
      t.budget_minor = (t.budget_minor ?? 0) + (r.finance.budget_minor ?? 0);
      t.actual_minor += r.finance.actual_minor;
      t.variance_minor = (t.variance_minor ?? 0) + (r.finance.variance_minor ?? 0);
      totals.set(r.finance.currency, t);
    }
    const counts = portfolioCounts(a);
    return {
      generated_at: stamp(),
      alerts: ordered
        .flatMap((r) => r.needs_you.map((n) => ({ ...ref(r), ...n, at: r.updated_at })))
        .sort((x, y) => (y.at ?? "").localeCompare(x.at ?? "")),
      health: ordered.map((r) => {
        const sc = card.get(r.company_id);
        return {
          ...ref(r),
          band: r.band,
          kpi_score: r.kpi_score,
          trend: r.trend,
          completeness: sc?.completeness ?? null,
          updated_at: r.updated_at,
          withheld: off(r, "kpi"),
        };
      }),
      kpis: ordered.map((r) => {
        const sc = card.get(r.company_id);
        const lines = [...(sc?.head_metrics ?? []), ...(sc?.company_metrics ?? [])].filter(
          (m) => m.target !== null && m.hit !== null,
        );
        return {
          ...ref(r),
          period_key: r.kpi_period,
          hit: lines.filter((m) => m.hit).length,
          miss: lines.filter((m) => !m.hit).length,
          misses: lines.filter((m) => !m.hit).map((m) => m.name),
          sharing: sc?.sharing ?? "metrics",
        };
      }),
      money: {
        rows: ordered.map((r) => ({
          ...ref(r),
          period_key: r.finance?.period_key ?? null,
          money: r.finance
            ? {
                currency: r.finance.currency,
                budget_minor: r.finance.budget_minor,
                actual_minor: r.finance.actual_minor,
                variance_minor: r.finance.variance_minor,
              }
            : null,
          withheld: off(r, "finance"),
        })),
        totals: [...totals.values()],
        cash_connected: false,
      },
      workforce: {
        // A viewer has no headcount kind: no workforce rows at all.
        rows: (level === "portfolio_viewer" ? [] : ordered).map((r) => ({
          ...ref(r),
          people: r.headcount.people,
          des: r.agents.des,
          des_live: r.agents.live,
          ags: 0,
          runs_7d: r.agents.runs_7d,
          failed_7d: 0,
          spend_month_micros: r.agents.spend_month_micros,
          hours_week_minutes: 0,
          approvals_waiting: r.agents.approvals_waiting,
          withheld: off(r, "headcount"),
        })),
      },
      priorities: {
        portfolio: { quarter: quarterOf(d.now()), ...counts },
        off_track: ordered.flatMap((r) =>
          r.priorities.off_track.map((p) => ({
            ...ref(r),
            goal_id: p.goal_id,
            title: p.title,
            track: p.track,
            owner_name: p.owner_name,
          })),
        ),
      },
    };
  }

  // The brief (services/api domain/portfolio/brief.ts `composeBrief`) ------------------------

  type LineKind = NonNullable<FounderBriefLine["kind"]>;
  /** The projection kind each needs-you chip is read from (a brief line's provenance). */
  const NEED_KIND: Record<NeedsYou["kind"], LineKind> = {
    kpi: "kpi",
    stale: "kpi",
    priority: "goals",
    escalation: "escalation",
    budget: "finance",
    approvals: "decisions",
    meeting: "meetings",
    vacancy: "headcount",
  };
  /** The kinds a portfolio viewer's grant reads. */
  const VIEWER_KINDS: readonly LineKind[] = ["kpi", "finance", "goals"];

  /**
   * A stored brief as the reader may see it now: a company line stays only while the company is
   * held and in the reader's subset, the reader's level reads the line's kind and the company
   * still shares it; a line without a kind (older briefs) only for an owner or member while the
   * company withholds nothing. A dropped line turns the headline neutral; an emptied section
   * says so.
   */
  function briefFor(a: Account, me: MeResponse, level: Level, b: FounderBrief): FounderBrief {
    let dropped = false;
    const keep = (l: FounderBriefLine) => {
      if (l.company_id === null) return true;
      const h = a.holdings.find((x) => x.company_id === l.company_id);
      const c = companyOf(l.company_id);
      if (!h || !c || !inSubset(a, me, c.id)) return false;
      const withheld = new Set<string>(withheldOf(sharingOf(c)));
      if (!l.kind) return level !== "portfolio_viewer" && withheld.size === 0;
      if (level === "portfolio_viewer" && !VIEWER_KINDS.includes(l.kind)) return false;
      return !withheld.has(l.kind);
    };
    const kept = b.sections.map((s) => {
      const lines = s.lines.filter(keep);
      if (lines.length < s.lines.length) dropped = true;
      return { ...s, lines };
    });
    // The headline counts every company (quiet ones too), so it shows only to an account-wide
    // owner or member, while no held company withholds anything, for a brief written after the
    // account's last company removal, and with no line cut; otherwise it is neutral.
    const whole =
      level !== "portfolio_viewer" &&
      !a.subsets[me.user.id] &&
      a.holdings.every((h) => {
        const c = companyOf(h.company_id);
        return !c || withheldOf(sharingOf(c)).length === 0;
      }) &&
      // Same millisecond as the removal: written after it (the twin's clock is coarse).
      (!a.removed_at || b.created_at >= a.removed_at);
    if (!dropped && whole) return b;
    // A partial brief: the lines that speak for the whole account ("Nothing is off.") go too;
    // an emptied section says so.
    const sections = kept.map((s) => {
      const lines = s.lines.filter((l) => !(l.company_id === null && WHOLE_LINES.has(l.text)));
      return {
        ...s,
        lines: lines.length
          ? lines
          : [{ text: "Nothing you can see here.", company_id: null, tone: "neutral" as const }],
      };
    });
    return { ...b, headline: `Brief · ${b.period_key}`, sections };
  }

  /** The brief's fallback lines, true only of the whole account (`composeBrief`'s `none`). */
  const WHOLE_LINES = new Set([
    "Nothing is off.",
    "No change since the last brief.",
    "No decision is waiting on you.",
    "Nothing scheduled.",
  ]);

  const money = (minor: number, currency: string) =>
    `${currency} ${(minor / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  function composeBrief(
    rows: readonly FocusRow[],
    portfolio: { off: number; at_risk: number; total: number },
  ): Pick<FounderBrief, "headline" | "sections"> {
    const changed: FounderBriefLine[] = [];
    const off: FounderBriefLine[] = [];
    const decisions: FounderBriefLine[] = [];
    const next: FounderBriefLine[] = [];
    for (const r of rows) {
      if (r.kpi_score !== null && r.trend && r.trend !== "flat")
        changed.push({
          text: `${r.name}: KPI ${r.trend === "up" ? "up" : "down"} to ${formatScore(r.kpi_score)}${
            r.kpi_period ? ` (${r.kpi_period})` : ""
          }`,
          company_id: r.company_id,
          tone: r.trend === "up" ? "good" : "bad",
          kind: "kpi",
        });
      for (const n of r.needs_you)
        if (n.kind !== "approvals")
          off.push({
            text: `${r.name}: ${n.text}`,
            company_id: r.company_id,
            tone: "bad",
            kind: NEED_KIND[n.kind],
          });
      for (const g of r.priorities.off_track.slice(0, 2))
        off.push({
          text: `${r.name} · ${g.title} (${g.track === "off" ? "off track" : "at risk"}${
            g.owner_name ? `, ${g.owner_name}` : ""
          })`,
          company_id: r.company_id,
          tone: "bad",
          kind: "goals",
        });
      if (r.agents.approvals_waiting > 0)
        decisions.push({
          text: `${r.name}: ${r.agents.approvals_waiting} approval${
            r.agents.approvals_waiting === 1 ? "" : "s"
          } waiting`,
          company_id: r.company_id,
          tone: "neutral",
          kind: "decisions",
        });
      for (const e of r.escalations.items)
        decisions.push({
          text: `${r.name}: decide on "${e.title}"${e.owner_name ? ` (raised by ${e.owner_name})` : ""}`,
          company_id: r.company_id,
          tone: "neutral",
          kind: "escalation",
        });
      if (r.finance && r.finance.budget_minor !== null)
        (r.finance.actual_minor > r.finance.budget_minor ? off : changed).push({
          text: `${r.name}: ${money(r.finance.actual_minor, r.finance.currency)} spent of ${money(
            r.finance.budget_minor,
            r.finance.currency,
          )} budget (${r.finance.period_key})`,
          company_id: r.company_id,
          tone: r.finance.actual_minor > r.finance.budget_minor ? "bad" : "neutral",
          kind: "finance",
        });
      if (r.meetings.next_at)
        next.push({
          text: `${r.name}: next meeting ${r.meetings.next_at.slice(0, 10)}`,
          company_id: r.company_id,
          tone: "neutral",
          kind: "meetings",
        });
    }
    if (portfolio.total > 0)
      next.push({
        text: `Portfolio priorities: ${portfolio.total - portfolio.off - portfolio.at_risk} of ${
          portfolio.total
        } on track${portfolio.off ? `, ${portfolio.off} off track` : ""}${
          portfolio.at_risk ? `, ${portfolio.at_risk} at risk` : ""
        }`,
        company_id: null,
        tone: portfolio.off ? "bad" : "neutral",
      });
    const needing = rows.filter((r) => r.needs_you.length > 0);
    const things = rows.reduce((n, r) => n + r.needs_you.length, 0);
    const headline =
      things === 0
        ? `All quiet across ${rows.length} compan${rows.length === 1 ? "y" : "ies"}`
        : `${things} thing${things === 1 ? "" : "s"} need${things === 1 ? "s" : ""} you across ${
            needing.length
          } compan${needing.length === 1 ? "y" : "ies"}`;
    const none = (text: string): FounderBriefLine[] => [
      { text, company_id: null, tone: "neutral" },
    ];
    return {
      headline,
      sections: [
        { key: "off", title: "What's off", lines: off.length ? off : none("Nothing is off.") },
        {
          key: "changed",
          title: "What changed",
          lines: changed.length ? changed : none("No change since the last brief."),
        },
        {
          key: "decisions",
          title: "Decisions waiting",
          lines: decisions.length ? decisions : none("No decision is waiting on you."),
        },
        { key: "next", title: "Coming up", lines: next.length ? next : none("Nothing scheduled.") },
      ],
    };
  }

  // The switcher -----------------------------------------------------------------------------

  function meCompanies(me: MeResponse): MeCompanies {
    const a = me.portfolio
      ? (accounts.find((x) => x.id === me.portfolio?.account_id) ?? null)
      : null;
    if (a) ensureWorld(a, me);
    const rows = new Map<string, SwitcherCompany>();
    // FOUNDER-OS: the account's holdings only while the caller is still a member of its
    // portfolio company, and only those in their grant's subset; any other company is an
    // ordinary membership row with no portfolio band, position or relation.
    const admitted = !!a && me.memberships.some((m) => m.company_id === a.portfolio_company_id);
    const visible = a && admitted ? holdingsFor(a, me) : [];
    const holding = (id: string) => visible.find((h) => h.company_id === id) ?? null;
    const demo = a?.id === PORTFOLIO.accountId;
    const base = (c: FakeCompany, group: SwitcherCompany["group"], agency: string | null) => {
      const h = holding(c.id);
      const pr = PROJECTIONS[c.id];
      return {
        company_id: c.id,
        slug: c.slug,
        name: c.name,
        kind: c.kind,
        group,
        agency_name: agency,
        unread: demo && pr ? pr.unread : 0,
        approvals: demo && pr ? pr.approvals : 0,
        // The band of the newest KPI week, unless the company withholds its KPIs.
        band: h && pr && sharingOf(c).kpi !== "off" ? pr.band : null,
        position: h ? h.position : null,
      } satisfies SwitcherCompany;
    };
    for (const m of me.memberships) {
      const c = companyOf(m.company_id);
      if (!c) continue;
      const h = holding(c.id);
      const group =
        c.kind === "portfolio"
          ? "portfolio"
          : h?.relation === "client"
            ? "client"
            : h?.relation === "owned" || m.roles.includes("owner")
              ? "owned"
              : "member";
      rows.set(c.id, base(c, group, null));
    }
    const agency = me.agency ? (AGENCY_NAMES[me.agency.agency_id] ?? "your agency") : null;
    for (const g of me.agency?.grants ?? []) {
      const c = companyOf(g.company_id);
      if (!c) continue;
      const existing = rows.get(c.id);
      if (existing) {
        if (existing.group === "client") existing.agency_name = agency;
        continue;
      }
      rows.set(c.id, base(c, "client", agency));
    }
    const rank = { portfolio: 0, owned: 1, member: 1, client: 2 } as const;
    const pos = (x: SwitcherCompany) => x.position ?? Number.MAX_SAFE_INTEGER;
    const companies = [...rows.values()].sort(
      (x, y) => rank[x.group] - rank[y.group] || pos(x) - pos(y) || x.name.localeCompare(y.name),
    );
    const portfolio = a ? companyOf(a.portfolio_company_id) : null;
    return {
      account:
        a && portfolio && me.portfolio
          ? {
              id: a.id,
              name: a.name,
              portfolio_company_id: portfolio.id,
              portfolio_slug: portfolio.slug,
              level: me.portfolio.level,
            }
          : null,
      companies,
    };
  }

  // Handlers ---------------------------------------------------------------------------------

  const handlers: Partial<Record<RouteId, Handler>> = {
    "me.companies": ({ me }) => json(meCompanies(me)),
    // FOUNDER-OS phase 1: what the company shares now (`sharingView`, the views' own rule).
    "companies.founderSharing": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return forbidden();
      return json(sharingView(principal.company));
    },
    "accounts.create": ({ me, body }) => {
      if (me.portfolio) return conflict("You already have an account.");
      const b = (body ?? {}) as { name?: unknown };
      if (b.name !== undefined && (typeof b.name !== "string" || !b.name.trim()))
        return validation("name must not be empty");
      const n = accounts.length + ++seq;
      const company = portfolioCompany(uid(0xf0c0, n), `founder-os-${n}`);
      d.companies.push(company);
      const a: Account = {
        id: uid(0xacc0, n + 1),
        name: typeof b.name === "string" ? b.name.trim() : me.user.name,
        owner_user_id: me.user.id,
        portfolio_company_id: company.id,
        founder_membership: uid(0xf0f0, 0x100 + n),
        founder_seat: uid(0xf05e, 0x100 + n * 2),
        chief_seat: uid(0xf05e, 0x101 + n * 2),
        holdings: [],
        links: [],
        briefs: [],
        seeded: true,
        subsets: {},
      };
      accounts.push(a);
      me.memberships.push({
        company_id: company.id,
        company_slug: company.slug,
        company_name: company.name,
        company_calendar: company.calendar_config,
        membership_id: a.founder_membership,
        kind: "person",
        roles: ["owner"],
      });
      me.entitlements[company.id] = { core: "active", kpi: "active", chat: "active" };
      me.portfolio = { account_id: a.id, company_id: company.id, level: "owner" };
      return json(
        {
          id: a.id,
          name: a.name,
          portfolio_company_id: company.id,
          portfolio_slug: company.slug,
          level: "owner",
        } satisfies AccountRef,
        201,
      );
    },
    "accounts.companies.add": ({ me, params, body }) => {
      const a = accounts.find((x) => x.id === params.account_id);
      if (!a || !levelOn(me, a)) return notFound();
      // Admission to the portfolio company as one of its managers, then the account-wide owner.
      const seat = me.memberships.find((m) => m.company_id === a.portfolio_company_id);
      if (!seat) return notFound();
      if (!seat.roles.some((r) => r === "owner" || r === "admin" || r === "agency_operator"))
        return forbidden("A manager of the portfolio company only.");
      if (!writesOn(me, a)) return forbidden("The account owner only.");
      ensureWorld(a, me);
      const b = (body ?? {}) as {
        company_id?: unknown;
        create?: { name?: unknown; slug?: unknown };
      };
      if (b.create !== undefined && b.company_id !== undefined)
        return validation("company_id or create, not both");
      // Wave 12: a new company the founder owns, made first, then added like any other.
      if (b.create !== undefined) {
        const name = typeof b.create?.name === "string" ? b.create.name.trim() : "";
        if (!name) return validation("create.name must not be empty");
        const chosen = typeof b.create.slug === "string" ? b.create.slug : null;
        const base =
          chosen ??
          (name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 50) ||
            "company");
        const taken = (s: string) => d.companies.some((c) => c.slug === s);
        if (chosen && taken(chosen)) return conflict("The slug is taken.");
        const n = d.companies.length + ++seq;
        const slug = taken(base) ? `${base}-${String(n).padStart(6, "0")}` : base;
        const portfolio = companyOf(a.portfolio_company_id);
        const made: FakeCompany = {
          id: uid(0xc0de, 0x200 + n),
          slug,
          name,
          kind: "operating",
          settings: {},
          calendar_config: portfolio?.calendar_config ?? {
            timezone: "UTC",
            weekStartDay: 1,
            fiscalYearStartMonth: 1,
          },
          reporting_currency: portfolio?.reporting_currency ?? "USD",
          // a founder's own company (no agency created it): self-serve, as the API's default
          agent_mode: "self_serve",
          created_at: stamp(),
          updated_at: stamp(),
        };
        d.companies.push(made);
        me.memberships.push({
          company_id: made.id,
          company_slug: made.slug,
          company_name: made.name,
          company_calendar: made.calendar_config,
          membership_id: uid(0xc0df, 0x200 + n),
          kind: "person",
          roles: ["owner"],
        });
        me.entitlements[made.id] = { core: "active" };
        b.company_id = made.id;
      }
      const id = b.company_id;
      if (typeof id !== "string") return validation("company_id is required");
      const c = companyOf(id);
      if (!c) return notFound();
      const owns = me.memberships.some((m) => m.company_id === id && m.roles.includes("owner"));
      const serves = (me.agency?.grants ?? []).some(
        (g) => g.company_id === id && g.level === "operator",
      );
      if (!owns && !serves)
        return forbidden("You neither own this company nor serve it through your agency.");
      if (c.kind === "portfolio") return conflict("A portfolio company cannot be added.");
      if (a.holdings.some((h) => h.company_id === id))
        return conflict("The company is already in the portfolio.");
      const h: Holding = {
        company_id: id,
        relation: owns ? "owned" : "client",
        position: a.holdings.reduce((m, x) => Math.max(m, x.position + 1), 0),
        added_at: stamp(),
      };
      a.holdings.push(h);
      // Its head seat linked under the founder, when the company shares one.
      makeLink(h, c, a.founder_seat);
      return json(portfolioCompanyRow(h, "owner"), 201);
    },
    "portfolio.companies.list": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      return json({
        data: [...holdingsFor(s.a, principal.me)]
          .sort((x, y) => x.position - y.position)
          .flatMap((h) => portfolioCompanyRow(h, s.level) ?? []),
      });
    },
    "portfolio.companies.remove": ({ principal, params }) => {
      const s = standing(principal, true);
      if (s instanceof Response) return s;
      const before = s.a.holdings.length;
      s.a.holdings = s.a.holdings.filter((h) => h.company_id !== params.company_id);
      if (s.a.holdings.length === before) return notFound();
      s.a.removed_at = stamp();
      s.a.links = s.a.links.filter((l) => l.company_id !== params.company_id);
      return noContent();
    },
    "portfolio.focus": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      const rows = focusOf(s.a, principal.me, s.level);
      const counts = portfolioCounts(s.a);
      return json({
        account_id: s.a.id,
        generated_at: stamp(),
        needs_you_count: rows.reduce((n, r) => n + r.needs_you.length, 0),
        companies: rows,
        portfolio_priorities: { quarter: quarterOf(d.now()), ...counts },
      } satisfies PortfolioFocus);
    },
    "portfolio.chart": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      const c = principal.company;
      const seats = d
        .seatsOf(c)
        .filter((x) => x.status !== "proposed" && x.status !== "closed")
        .map((x) => {
          const h = holderOf(c, x.id);
          return {
            seat_id: x.id,
            title: x.title,
            holder_name: h ? d.nameOf(c, h.membership_id) : null,
            supervisor_seat_id: x.supervisor_seat_id,
          };
        });
      const founder =
        seats.find((x) => x.seat_id === s.a.founder_seat) ??
        seats.find((x) => x.supervisor_seat_id === null) ??
        null;
      const rows = new Map(focusOf(s.a, principal.me, s.level).map((r) => [r.company_id, r]));
      const live = new Set(seats.map((x) => x.seat_id));
      return json({
        founder_seat: founder,
        seats: seats.filter((x) => x.seat_id !== founder?.seat_id),
        linked: founder
          ? [...holdingsFor(s.a, principal.me)]
              .sort((x, y) => x.position - y.position)
              .flatMap((h) => {
                const co = companyOf(h.company_id);
                const r = rows.get(h.company_id);
                if (!co || !r) return [];
                const withheld = r.withheld ?? [];
                const noHead = withheld.includes("headcount") || s.level === "portfolio_viewer";
                return [
                  {
                    linked_seat_id: linkIdOf(h),
                    portfolio_seat_id:
                      h.link && live.has(h.link.parent) ? h.link.parent : founder.seat_id,
                    company_id: co.id,
                    slug: co.slug,
                    name: co.name,
                    relation: h.relation,
                    band: r.band,
                    head_seat: noHead ? null : headSeatOf(co),
                    priorities: {
                      total: r.priorities.total,
                      off: r.priorities.off,
                      at_risk: r.priorities.at_risk,
                    },
                    headcount: r.headcount,
                    // FOUNDER-OS phase 1: the lenses over the card.
                    kpi_score: r.kpi_score,
                    trend: r.trend,
                    finance: r.finance
                      ? {
                          currency: r.finance.currency,
                          budget_minor: r.finance.budget_minor,
                          actual_minor: r.finance.actual_minor,
                          variance_minor: r.finance.variance_minor,
                        }
                      : null,
                    agents: { des: r.agents.des, live: r.agents.live },
                    withheld,
                    updated_at: r.updated_at,
                  },
                ];
              })
          : [],
        // An owner grant limited to some companies reads only.
        can_edit: principal.manager && writesOn(principal.me, s.a),
      } satisfies PortfolioChart);
    },
    "portfolio.linkedSeats.update": ({ principal, params, body }) => {
      const s = standing(principal, true);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      // `id`: a live link of the account, or a held company (its link, or none yet); anything
      // else, a link that no longer exists included, is 404.
      const h =
        s.a.holdings.find((x) => x.link?.id === params.id) ??
        s.a.holdings.find((x) => x.company_id === params.id);
      const co = h ? companyOf(h.company_id) : null;
      if (!h || !co) return notFound();
      const seatId = (body as { portfolio_seat_id?: unknown } | undefined)?.portfolio_seat_id;
      const seat = d
        .seatsOf(principal.company)
        .find((x) => x.id === seatId && (x.status === "open" || x.status === "filled"));
      if (!seat) return validation("portfolio_seat_id: an open or filled seat of the portfolio");
      // No link yet: it is made from the head seat the company shares (409 without one).
      if (h.link) h.link.parent = seat.id;
      else if (!makeLink(h, co, seat.id))
        return conflict("The company has no link and shares no head seat.");
      return json({
        id: linkIdOf(h),
        company_id: co.id,
        portfolio_seat_id: seat.id,
      } satisfies LinkedSeatRef);
    },
    "portfolio.scorecard": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      return json(scorecardOf(s.a, principal.me, s.level));
    },
    "portfolio.live": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      return json(liveOf(s.a, principal.me, s.level));
    },
    "portfolio.escalations": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      if (s.level === "portfolio_viewer") return json({ items: [] });
      // FOUNDER-OS phase 1: the caller's companies that share their escalations.
      const items = holdingsFor(s.a, principal.me)
        .flatMap((h) => {
          const c = companyOf(h.company_id);
          return c && sharingOf(c).escalation ? escalationsOf(principal.me, c) : [];
        })
        .sort((x, y) => (y.escalated_at ?? "").localeCompare(x.escalated_at ?? ""));
      return json({ items });
    },
    "portfolio.goals": ({ principal, url }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      const quarter = url.searchParams.get("quarter") ?? quarterOf(d.now());
      if (!/^\d{4}-Q[1-4]$/.test(quarter)) return validation("quarter: a quarter key (2026-Q4)");
      return json({
        quarter,
        // Every state, dropped included (the real listing's).
        goals: portfolioGoalsOf(s.a, quarter).map((g) => goalView(s.a, g, principal.me)),
      });
    },
    "portfolio.goalLinks.create": ({ principal, body }) => {
      const s = standing(principal, true);
      if (s instanceof Response) return s;
      const b = (body ?? {}) as Record<string, unknown>;
      const [pgId, coId, goalId] = [b.portfolio_goal_id, b.company_id, b.company_goal_id];
      if (typeof pgId !== "string" || typeof coId !== "string" || typeof goalId !== "string")
        return validation("portfolio_goal_id, company_id and company_goal_id are required");
      const portfolio = companyOf(s.a.portfolio_company_id);
      const pg = portfolio ? d.goalsOf(portfolio).find((g) => g.id === pgId) : undefined;
      if (pg?.kind !== "priority") return validation("portfolio_goal_id: no such priority");
      if (!s.a.holdings.some((h) => h.company_id === coId))
        return validation("company_id: the company is not in the portfolio");
      const c = companyOf(coId);
      if (!c) return notFound();
      // Only from the company's shared `goals` projection: its priorities of this quarter
      // (dropped ones included, as the projection carries them); withheld goals: 422.
      if (!sharingOf(c).goals)
        return validation("company_goal_id: the company does not share its priorities");
      const quarter = quarterOf(d.now());
      const g = d
        .goalsOf(c)
        .find((x) => x.id === goalId && x.kind === "priority" && x.period_key === quarter);
      if (!g) return validation("company_goal_id: not a shared priority of that company");
      if (
        s.a.links.some(
          (l) =>
            l.portfolio_goal_id === pgId && l.company_id === coId && l.company_goal_id === goalId,
        )
      )
        return conflict("Already linked.");
      const link: GoalLink = {
        id: uid(0xf011, 0x100 + s.a.links.length + ++seq),
        portfolio_goal_id: pgId,
        company_id: coId,
        company_goal_id: goalId,
        created_at: stamp(),
      };
      s.a.links.push(link);
      return json(link, 201);
    },
    "portfolio.goalLinks.delete": ({ principal, params }) => {
      const s = standing(principal, true);
      if (s instanceof Response) return s;
      const before = s.a.links.length;
      s.a.links = s.a.links.filter((l) => l.id !== params.id);
      return s.a.links.length === before ? notFound() : noContent();
    },
    "portfolio.briefs.list": ({ principal }) => {
      const s = standing(principal);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      // FOUNDER-OS: every standing lists the briefs, each cut per line to what it reads now.
      return json({
        data: [...s.a.briefs]
          .sort((x, y) => y.created_at.localeCompare(x.created_at))
          .slice(0, 30)
          .map((b) => briefFor(s.a, principal.me, s.level, b)),
      });
    },
    "portfolio.briefs.generate": ({ principal, body }) => {
      const s = standing(principal, true);
      if (s instanceof Response || !principal) return s instanceof Response ? s : notFound();
      const cadence = (body as { cadence?: unknown } | undefined)?.cadence ?? "daily";
      if (cadence !== "daily" && cadence !== "weekly")
        return validation("cadence: daily or weekly");
      const now = d.now();
      const brief: FounderBrief = {
        company_id: s.a.portfolio_company_id,
        id: uid(0xf0b0, 0x100 + ++seq),
        cadence,
        period_key: cadence === "daily" ? now.toISOString().slice(0, 10) : isoWeekKey(now),
        generated_by: "system",
        ...composeBrief(focusOf(s.a, principal.me, s.level), portfolioCounts(s.a)),
        created_at: new Date(
          Math.max(now.getTime(), ...s.a.briefs.map((x) => Date.parse(x.created_at) + 1)),
        ).toISOString(),
      };
      s.a.briefs.push(brief);
      // Answered cut exactly as the list reads it.
      return json(briefFor(s.a, principal.me, s.level, brief), 201);
    },
  };

  /** Tests: limit a grant holder to some companies of the demo account (null: all of them). */
  const setSubset = (userId: string, companyIds: string[] | null) => {
    for (const a of accounts)
      if (companyIds) a.subsets[userId] = companyIds;
      else delete a.subsets[userId];
  };

  return { handlers, structureOf, goalsSeedOf, meCompanies, setSubset };
}
